import React, { useMemo, useRef, useState } from 'react';

const B = '#2563eb';

// Bilan par ANNÉE CIVILE (1er janvier → 31 décembre), façon "Spotify
// Wrapped" — vraiment à sa place en fin d'année, comme l'original, plutôt
// qu'une fenêtre glissante de 12 mois qui n'a jamais de "fin" marquée.
// Pensé pour être partagé (export PNG) plutôt que pour rester dans l'app :
// contrairement au reste de PlanifPro, on privilégie ici l'esthétique sur
// la densité d'info.
export default function CareerWrapped({ user, interventions, factures, clientsList, darkMode, onClose }) {
  const cardRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [annee, setAnnee] = useState(new Date().getFullYear());

  const stats = useMemo(() => {
    const debut = new Date(annee, 0, 1);
    const fin = new Date(annee, 11, 31, 23, 59, 59);

    const facturesPeriode = (factures || []).filter(f => {
      if (f.status !== 'paye' || f.type === 'avoir' || !f.date) return false;
      const d = new Date(f.date);
      return d >= debut && d <= fin;
    });
    const ca = facturesPeriode.reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
    const plusGros = facturesPeriode.reduce((max, f) => Math.max(max, parseFloat(f.amount) || 0), 0);

    const interventionsPeriode = (interventions || []).filter(i => {
      if (i.statut !== 'termine' || !i.date) return false;
      const d = new Date(i.date);
      return d >= debut && d <= fin;
    });
    const heures = interventionsPeriode.reduce((s, i) => s + (parseFloat(i.duree) || 0), 0) / 60;

    const nouveauxClients = (clientsList || []).filter(c => {
      if (!c.created_at) return false;
      const d = new Date(c.created_at);
      return d >= debut && d <= fin;
    }).length;

    // Meilleur mois : regroupement du CA payé par mois calendaire.
    const parMois = {};
    facturesPeriode.forEach(f => {
      const d = new Date(f.date);
      const cle = d.getMonth();
      parMois[cle] = (parMois[cle] || 0) + (parseFloat(f.amount) || 0);
    });
    let meilleurMoisLabel = null, meilleurMoisCa = 0;
    const NOMS_MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    Object.entries(parMois).forEach(([mois, montant]) => {
      if (montant > meilleurMoisCa) {
        meilleurMoisCa = montant;
        meilleurMoisLabel = NOMS_MOIS[Number(mois)];
      }
    });

    return {
      ca, plusGros, heures,
      interventionsTerminees: interventionsPeriode.length,
      nouveauxClients,
      meilleurMoisLabel, meilleurMoisCa,
    };
  }, [interventions, factures, clientsList, annee]);

  // Pas de données avant la création du compte : borne la navigation pour
  // éviter d'afficher un bilan 100% vide sur une année sans historique.
  const premiereAnnee = user?.createdat ? new Date(user.createdat).getFullYear() : annee;
  const anneeCourante = new Date().getFullYear();

  const telechargerImage = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: '#0f172a' });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `planifpro-wrapped-${annee}.png`;
      a.click();
    } catch (err) {
      alert("Erreur lors de la génération de l'image : " + err.message);
    } finally {
      setExporting(false);
    }
  };

  const Stat = ({ emoji, label, value }) => (
    <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '18px', padding: '18px 20px', marginBottom: '12px' }}>
      <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.65)', margin: '0 0 4px', fontWeight: 600 }}>{emoji} {label}</p>
      <p style={{ fontSize: '28px', color: '#fff', margin: 0, fontWeight: 800, letterSpacing: '-0.02em' }}>{value}</p>
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,26,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: '24px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>

        <div ref={cardRef} style={{
          width: '360px', borderRadius: '28px', padding: '36px 28px',
          background: 'linear-gradient(160deg, #0f172a 0%, #1e3a8a 55%, #2563eb 100%)',
          boxShadow: '0 30px 80px rgba(0,0,0,0.4)', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 4px' }}>PlanifPro Wrapped {annee}</p>
          <h2 style={{ fontSize: '24px', color: '#fff', fontWeight: 800, margin: '0 0 4px', letterSpacing: '-0.02em' }}>{user?.entrepriseNom || 'Votre activité'}</h2>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', margin: '0 0 24px' }}>Votre année {annee}</p>

          <Stat emoji="💰" label="Chiffre d'affaires encaissé" value={`${stats.ca.toFixed(0)} €`} />
          <Stat emoji="✅" label="Interventions terminées" value={stats.interventionsTerminees} />
          <Stat emoji="⏱️" label="Heures travaillées" value={`${Math.round(stats.heures)} h`} />
          <Stat emoji="👥" label="Nouveaux clients" value={stats.nouveauxClients} />
          {stats.meilleurMoisLabel && <Stat emoji="🏆" label="Meilleur mois" value={`${stats.meilleurMoisLabel[0].toUpperCase() + stats.meilleurMoisLabel.slice(1)}`} />}
          {stats.plusGros > 0 && <Stat emoji="💎" label="Plus gros contrat" value={`${stats.plusGros.toFixed(0)} €`} />}

          <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', margin: '20px 0 0' }}>
            Généré via <strong style={{ color: 'rgba(255,255,255,0.6)' }}>PlanifPro</strong>
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button onClick={() => setAnnee(a => a - 1)} disabled={annee <= premiereAnnee} style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: annee <= premiereAnnee ? 'default' : 'pointer', opacity: annee <= premiereAnnee ? 0.35 : 1, fontSize: '14px' }}>◀</button>
          <span style={{ color: '#fff', fontSize: '14px', fontWeight: 700 }}>{annee}</span>
          <button onClick={() => setAnnee(a => a + 1)} disabled={annee >= anneeCourante} style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: annee >= anneeCourante ? 'default' : 'pointer', opacity: annee >= anneeCourante ? 0.35 : 1, fontSize: '14px' }}>▶</button>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={telechargerImage} disabled={exporting} style={{ padding: '12px 24px', background: B, color: '#fff', border: 'none', borderRadius: '980px', cursor: exporting ? 'wait' : 'pointer', fontWeight: 700, fontSize: '14px' }}>
            {exporting ? 'Génération...' : '📥 Télécharger l\'image'}
          </button>
          <button onClick={onClose} style={{ padding: '12px 20px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>
            Fermer
          </button>
        </div>
        <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', textAlign: 'center', maxWidth: '340px' }}>
          Télécharge l'image et poste-la toi-même sur LinkedIn ou Instagram.
        </p>
      </div>
    </div>
  );
}
